import { Capacitor } from '@capacitor/core';

// Guest invites are NOT sent by any server. The unofficial WhatsApp-Web gateway
// (anuplights.in:8096) is retired and must not be used; the only automated
// WhatsApp channel in this product is the Meta API used by the backend for OTP.
// An invite simply opens WhatsApp's official click-to-chat link (wa.me) with the
// text prefilled, and the member sends it from their own WhatsApp account.
const imageAsset = `${process.env.PUBLIC_URL || ''}/invites/image-invite.jpeg`;
const pdfAsset = `${process.env.PUBLIC_URL || ''}/invites/pdf-invite.pdf`;
const UPLOAD_URL = 'https://uploads.anuplights.com/upload';

const clean = (v) => String(v || '').trim();
const ten = (v) => clean(v).replace(/\D/g, '').slice(-10);
const waRecipientNumber = (v) => {
  const n = ten(v);
  return n ? `91${n}` : '';
};
const normalizeMediaUrl = (url) => clean(url);
const fileNameFromUrl = (url, fallback = 'invite') => {
  const mediaUrl = normalizeMediaUrl(url);
  if (!mediaUrl) return fallback;
  try {
    const lastSegment = new URL(mediaUrl).pathname.split('/').filter(Boolean).pop();
    return decodeURIComponent(lastSegment || fallback);
  } catch {
    const lastSegment = mediaUrl.split('?')[0].split('#')[0].split('/').filter(Boolean).pop();
    return decodeURIComponent(lastSegment || fallback);
  }
};

export const inviteMediaKey = (kind, event, organiser) => [
  'se_invite_media',
  organiser?.OrganiserId || event?.OrganiserId || 'org',
  event?.EventId || 'event',
  kind,
].join(':');

export const getInviteMedia = (kind, event, organiser) => {
  try {
    return JSON.parse(localStorage.getItem(inviteMediaKey(kind, event, organiser))) || null;
  } catch {
    return null;
  }
};

export const setInviteMedia = (kind, event, organiser, media) => {
  localStorage.setItem(inviteMediaKey(kind, event, organiser), JSON.stringify(media));
};

const firstUrl = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const match = value.match(/https?:\/\/[^\s"'<>]+/);
    if (match) return normalizeMediaUrl(match[0]);
    if (value.startsWith('/uploads/')) return `https://uploads.anuplights.com${value}`;
    return '';
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstUrl(item);
      if (found) return found;
    }
    return '';
  }
  if (typeof value === 'object') {
    for (const key of ['fileUrl', 'FileUrl', 'url', 'Url', 'URL', 'path', 'Path', 'FilePath', 'ImageUrl']) {
      const found = firstUrl(value[key]);
      if (found) return found;
    }
    for (const item of Object.values(value)) {
      const found = firstUrl(item);
      if (found) return found;
    }
  }
  return '';
};

export async function uploadInviteFile(file, kind, event, organiser) {
  const form = new FormData();
  form.append('service', 'smartify');
  form.append('file', file);

  const resp = await fetch(UPLOAD_URL, {
    method: 'POST',
    body: form,
  });
  const text = await resp.text();
  let data = text;
  try { data = JSON.parse(text); } catch { /* response may be plain text */ }
  if (!resp.ok) throw new Error(`Upload failed: ${resp.status}`);
  const url = firstUrl(data);
  if (!url) throw new Error('Upload did not return a file URL');
  const media = { url: normalizeMediaUrl(url), name: file.name, kind, uploadedAt: new Date().toISOString() };
  setInviteMedia(kind, event, organiser, media);
  return media;
}

export const inviteTextForGuest = (row, event) => {
  const name = clean([row.Surname, row.GuestName].filter(Boolean).join(' ')) || 'Guest';
  const eventName = clean(event?.EventName) || 'our function';
  const functions = clean(row.FunctionNames || row.FunctionName);
  return [
    `Dear ${name},`,
    `You are warmly invited to ${eventName}${functions ? ` (${functions})` : ''}.`,
    'Please join us and bless the occasion.',
  ].join('\n');
};

// Open WhatsApp's official click-to-chat link. On the APK `_system` hands the
// URL to Android, which opens the WhatsApp app; on the web it opens WhatsApp Web.
// Every hand-off to WhatsApp goes through here. On the web the window is
// NAMED, so the tab opened for the previous message is reused and focused
// instead of piling up a tab per guest; the APK hands the link to the system,
// which brings the installed app forward.
export function openWhatsAppUrl(url) {
  if (Capacitor.isNativePlatform()) {
    window.open(url, '_system');
    return;
  }
  const win = window.open(url, 'mehfil-whatsapp');
  if (!win) { window.location.href = url; return; }
  try { win.focus(); } catch { /* the browser decides */ }
}

function openWhatsAppChat({ phone, message }) {
  const number = waRecipientNumber(phone);
  if (!number) throw new Error('Guest mobile number is required');
  openWhatsAppUrl(`https://wa.me/${number}?text=${encodeURIComponent(message)}`);
}

async function assetToFile(url, fileName, mimeType) {
  const absoluteUrl = new URL(url, window.location.href).toString();
  const res = await fetch(absoluteUrl);
  if (!res.ok) throw new Error(`Could not load invite asset: ${res.statusText}`);
  const blob = await res.blob();
  return new File([blob], fileName, { type: mimeType || blob.type });
}

async function uploadInviteAsset(kind, row, event, organiser) {
  const saved = getInviteMedia(kind, event, organiser);
  if (saved?.url) {
    const normalized = normalizeMediaUrl(saved.url);
    const media = {
      ...saved,
      url: normalized,
      name: saved.name || fileNameFromUrl(normalized, `${kind}-invite`),
    };
    if (normalized !== saved.url || !saved.name) setInviteMedia(kind, event, organiser, media);
    return media;
  }

  const isPdf = kind === 'pdf';
  const file = await assetToFile(
    isPdf ? pdfAsset : imageAsset,
    isPdf ? 'pdf-invite.pdf' : 'image-invite.jpeg',
    isPdf ? 'application/pdf' : 'image/jpeg'
  );
  const media = await uploadInviteFile(file, kind, event || { EventId: row.EventId }, organiser);
  return media;
}

export async function shareInvite(kind, row, event, organiser) {
  const text = inviteTextForGuest(row, event);
  const phone = ten(row.MobileNumber);
  if (kind === 'text') {
    openWhatsAppChat({ phone, message: text });
    return;
  }
  // Image / PDF: WhatsApp click-to-chat cannot attach files, so the media link
  // (uploaded once per event, see Invite Forms) is appended to the message.
  const media = await uploadInviteAsset(kind, row, event, organiser);
  const label = kind === 'pdf' ? 'Invitation card (PDF)' : 'Invitation card';
  openWhatsAppChat({ phone, message: `${text}\n\n${label}: ${media.url}` });
}
